// frontend-react/src/components/TextHighlight.tsx
import React, { useMemo } from 'react';

// 高亮範圍接口
export interface HighlightRange {
  start: number;
  end: number;
  type: string;
  label?: string;
  color?: string;
  attributes?: Record<string, any>;
}

// 組件屬性接口
interface TextHighlightProps {
  text: string;
  ranges: HighlightRange[];
  onHighlightClick?: (range: HighlightRange, text: string) => void;
  onHighlightHover?: (range: HighlightRange, text: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

const TextHighlight: React.FC<TextHighlightProps> = ({
  text,
  ranges,
  onHighlightClick,
  onHighlightHover,
}) => {
  const getDefaultColor = (type: string): string => {
    const colors: Record<string, string> = {
      'risk-high': 'rgba(159, 53, 58, 0.3)', // 燕脂
      'risk-medium': 'rgba(252, 186, 77, 0.3)', // 琥珀
      'risk-low': 'rgba(27, 129, 62, 0.3)', // 常磐
      'risk-default': 'rgba(252, 186, 77, 0.3)', // 琥珀
      keyword: '#fff2e6',
      entity: '#f0f9fb',
      error: '#fef2f2',
      success: '#f0fdf4',
      warning: '#fff7ed',
      info: '#eef2ff',
    };
    return colors[type] || '#f5f5f5';
  };

  const getBorderColor = (type: string): string => {
    const colors: Record<string, string> = {
      'risk-high': 'var(--color-error)',
      'risk-medium': 'var(--color-warning)',
      'risk-low': 'var(--color-success)',
      'risk-default': 'var(--color-warning)',
      keyword: 'var(--color-warning)',
      entity: 'var(--color-primary)',
      error: 'var(--color-error)',
      success: 'var(--color-success)',
      warning: 'var(--color-warning)',
      info: 'var(--color-primary)',
    };
    return colors[type] || 'var(--color-border)';
  };

  const generateHighlightedText = useMemo(() => {
    if (!ranges.length) return [<span key='text-only'>{text}</span>];

    // Sort ranges by start position and filter valid ones
    const sortedRanges = ranges
      .filter(range => range.start < range.end && range.start >= 0 && range.end <= text.length)
      .sort((a, b) => a.start - b.start);

    if (!sortedRanges.length) return [<span key='text-only'>{text}</span>];

    const elements: JSX.Element[] = [];
    let lastIndex = 0;

    sortedRanges.forEach((range, index) => {
      // Add normal text before highlight
      if (lastIndex < range.start) {
        elements.push(<span key={`text-${index}`}>{text.slice(lastIndex, range.start)}</span>);
      }

      // Add highlighted text
      const highlightText = text.slice(range.start, range.end);
      if (highlightText) {
        // SonarQube Fix: Add accessibility attributes if interactive
        const isInteractive = !!onHighlightClick;

        const highlightElement = (
          <span
            key={`highlight-${index}`}
            className={`highlight highlight-${range.type}`}
            style={{
              backgroundColor: range.color || getDefaultColor(range.type),
              border: `1px solid ${getBorderColor(range.type)}`,
              borderRadius: '3px',
              padding: '1px 2px',
              cursor: isInteractive ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
            onClick={() => onHighlightClick?.(range, highlightText)}
            onMouseEnter={() => onHighlightHover?.(range, highlightText)}
            role={isInteractive ? 'button' : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onKeyDown={e => {
              if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onHighlightClick?.(range, highlightText);
              }
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 2px 8px ${getBorderColor(range.type)}40`;
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {highlightText}
          </span>
        );

        elements.push(highlightElement);
      }

      lastIndex = range.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      elements.push(<span key='text-end'>{text.slice(lastIndex)}</span>);
    }

    return elements;
  }, [text, ranges, onHighlightClick, onHighlightHover]);

  return (
    <div className='text-highlight-container' style={{ lineHeight: 1.6 }}>
      {generateHighlightedText}
    </div>
  );
};

export default TextHighlight;
